import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { authApi, authTokenStorage, authUserStorage, persistAuthenticatedSession, TwoFactorChallengeResponse } from '../services/api';
import { EnvironmentBanner } from '../components/EnvironmentBanner';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [logoSrc, setLogoSrc] = useState<string | null>('/logo.svg');

    const handleLogoError = () => {
        if (logoSrc === '/logo.svg') setLogoSrc('/logo.png');
        else setLogoSrc(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const res = await authApi.login({ username, password });
            const data = res.data;

            if ('token' in data) {
                const { isAdmin } = persistAuthenticatedSession(data);
                navigate(isAdmin ? '/admin' : '/');
                return;
            }

            if ((data as TwoFactorChallengeResponse).requiresTwoFactor && data.challengeId) {
                authTokenStorage.clear();
                authUserStorage.clear();
                try {
                    sessionStorage.setItem('pending_two_factor', JSON.stringify({
                        challengeId: data.challengeId,
                        username,
                    }));
                } catch { }
                navigate('/two-factor', {
                    replace: true,
                    state: {
                        challengeId: data.challengeId,
                        username,
                    },
                });
                return;
            }

            setError('Resposta inválida do servidor.');
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            const apiErr = err?.response?.data?.error;
            if (apiErr === 'UnauthorizedError') setError('Usuário ou senha inválidos.');
            else setError(apiMsg || 'Falha ao autenticar.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 to-secondary-50">
            <EnvironmentBanner />
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-secondary-200 p-8">
                    <div className="flex items-center mb-6">
                        {logoSrc ? (
                            <img
                                src={logoSrc}
                                onError={handleLogoError}
                                alt="My Finances Logo"
                                className="h-10 w-10 mr-3 object-contain"
                            />
                        ) : (
                            <div className="h-10 w-10 mr-3 rounded-md bg-primary-600 flex items-center justify-center">
                                <Wallet className="h-6 w-6 text-white" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-xl font-bold text-secondary-900 leading-tight">My Finances</h1>
                            <p className="text-sm text-secondary-600">Acesse sua conta para continuar</p>
                        </div>
                    </div>
                    <h2 className="text-lg font-semibold text-secondary-900 mb-4">Entrar</h2>
                    {error && (
                        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700">
                            {error}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Usuário</label>
                            <input
                                className="input-field w-full"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Digite seu usuário"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Senha</label>
                            <input
                                className="input-field w-full"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Digite sua senha"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn-primary w-full disabled:opacity-50"
                            disabled={submitting}
                        >
                            {submitting ? 'Entrando...' : 'Entrar'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
